import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import axios from "axios";
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const PdfProtegidoViewer = ({ tipoReporte, idOrder, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [fileURL, setFileURL] = useState(null);
  const [zoomPercentage, setZoomPercentage] = useState(90);
  const valorPdf = `${process.env.REACT_APP_API_PETCOZOO}/reporteRecepcion/getPdfProtegido/${tipoReporte}`;

  const handleDownload = async () => {
    try {
      setLoading(true);
      // Paso 1: Generar el token
      const tokenResponse = await axios.post(
        `${process.env.REACT_APP_API_PETCOZOO}/reporteRecepcion/generarTokenPdf`,
        { idOrder },
        {
          headers: {
            authorization: Cookies.get("loginCookie"),
          },
        },
      );

      const tokenTemporal = tokenResponse.data.tokenTemporal;

      // Paso 2: Descargar el PDF usando el token
      const response = await axios.get(`${valorPdf}`, {
        headers: {
          "x-temporal-token": tokenTemporal,
        },
        responseType: "blob",
      });

      // Paso 3: Determinar el nombre del archivo según el tipo de reporte
      let nombreArchivo = "";
      if (tipoReporte === "1") {
        nombreArchivo = `Reporte_Recepcion_${idOrder}.pdf`;
      } else if (tipoReporte === "2") {
        nombreArchivo = `Reporte_24H_${idOrder}.pdf`;
      } else if (tipoReporte === "3") {
        nombreArchivo = `Reporte_48H_${idOrder}.pdf`;
      }

      // Paso 4: Crear URL temporal del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nombreArchivo);
      document.body.appendChild(link);
      link.click();

      // Paso 5: Limpiar
      link.remove();
      window.URL.revokeObjectURL(url);

      setLoading(false);

      Swal.fire({
        icon: "success",
        title: "Descarga exitosa",
        text: "El PDF se ha descargado correctamente",
        confirmButtonColor: "#00205B",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      setLoading(false);
      console.error("Error al descargar PDF:", error);
      // Manejo de errores más específico
      let mensajeError = "No se pudo descargar el PDF. Intenta nuevamente.";

      if (error.response) {
        if (error.response.status === 403) {
          mensajeError =
            "Token inválido o expirado. Por favor intenta de nuevo.";
        } else if (error.response.status === 404) {
          mensajeError = "No se encontró el PDF solicitado.";
        }
      }
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeError,
        confirmButtonColor: "#00205B",
        confirmButtonText: "Aceptar",
      });
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const cargarPdf = async () => {
    if (!idOrder || !Cookies.get("loginCookie")) return;

    try {
      const tokenResp = await axios.post(
        `${process.env.REACT_APP_API_PETCOZOO}/reporteRecepcion/generarTokenPdf`,
        { idOrder },
        {
          headers: {
            authorization: Cookies.get("loginCookie"),
          },
        },
      );

      const tokenTemporal = tokenResp.data.tokenTemporal;
      const pdfResp = await axios.get(`${valorPdf}`, {
        responseType: "arraybuffer",
        headers: {
          "x-temporal-token": tokenTemporal,
        },
      });

      const mimeType = pdfResp.headers["content-type"];
      const blob = new Blob([pdfResp.data], { type: mimeType });
      const fileUrl = URL.createObjectURL(blob);
      setFileURL(fileUrl);
      const loadingTask = pdfjs.getDocument(fileUrl);
      const pdf = await loadingTask.promise;
      const firstPage = await pdf.getPage(1);
      setLoading(false);
    } catch (err) {
      console.error("Error cargando PDF protegido:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPdf();
  }, [idOrder]);

  const handleZoomIn = () => {
    setZoomPercentage((prev) => Math.min(prev + 10, 150));
  };

  const handleZoomOut = () => {
    setZoomPercentage((prev) => Math.max(prev - 10, 50));
  };

  const getScale = () => {
    if (zoomPercentage <= 100) {
      return (zoomPercentage / 100) * 1.7;
    } else {
      const extra = (zoomPercentage - 100) / 100;
      return 1.7 + extra * 0.5;
    }
  };

  const TIPOS_REPORTE = {
    1: "de recepción",
    2: "de 24 horas",
    3: "de 48 horas",
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          bgcolor: "#00205b",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Box className="div-title-disc">
          <Typography variant="h6">
            Reporte {TIPOS_REPORTE[tipoReporte] || ""}
          </Typography>
        </Box>

        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="div-body-disc">
        {idOrder ? (
          <Box className="box-pdf">
            {fileURL ? (
              <Document
                file={fileURL}
                onLoadSuccess={onDocumentLoadSuccess}
                onContextMenu={(e) => e.preventDefault()}>
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  scale={getScale()}
                />
              </Document>
            ) : (
              <Typography>Error al cargar el PDF</Typography>
            )}
          </Box>
        ) : (
          <Typography>No se ha seleccionado ningún disclaimer.</Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1.5fr 1fr",
          px: 2,
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={handleZoomOut}
            disabled={zoomPercentage <= 50}
            size="small"
            sx={{ color: "#00205b" }}>
            <ZoomOutIcon />
          </IconButton>

          <Typography
            variant="caption"
            sx={{ minWidth: "50px", textAlign: "center" }}>
            {zoomPercentage}%
          </Typography>

          <IconButton
            onClick={handleZoomIn}
            disabled={zoomPercentage >= 150}
            size="small"
            sx={{ color: "#00205b" }}>
            <ZoomInIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}>
          <Button
            className={`${pageNumber > 1 ? "color-pag" : "text-gray"}`}
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber(pageNumber - 1)}>
            Anterior
          </Button>

          <Typography variant="body2">
            Página {pageNumber} de {numPages}
          </Typography>

          <Button
            className={`${pageNumber < numPages ? "color-pag" : "text-gray"}`}
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber(pageNumber + 1)}>
            Siguiente
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <button className="btn-download" onClick={handleDownload}>
            Descargar
          </button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PdfProtegidoViewer;
